import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { HeartPulse, Lock, RefreshCw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const ROWS = [["calories", "Calories", ""], ["fat_g", "Total fat", "g"], ["sat_fat_g", "Saturated fat", "g"], ["cholesterol_mg", "Cholesterol", "mg"], ["sodium_mg", "Sodium", "mg"], ["carbs_g", "Carbohydrates", "g"], ["fiber_g", "Fiber", "g"], ["sugar_g", "Sugars", "g"], ["protein_g", "Protein", "g"]];
const fmt = (v) => (v >= 100 ? Math.round(v).toLocaleString() : Math.round(v * 10) / 10);

export const Locked = ({ what, testid }) => (
  <div className="flex items-center gap-2 rounded-md border border-dashed border-[#2D3442] p-3 text-xs text-[#A8A090]" data-testid={testid}>
    <Lock className="h-3.5 w-3.5 text-[#F59E0B]" />{what} are included with paid plans.<Link to="/pricing" className="text-[#F59E0B] hover:underline">See plans</Link>
  </div>
);

const Table = ({ data, mode, servings }) => {
  const vals = mode === "serving" ? data.per_serving : Object.fromEntries(Object.entries(data.per_serving).map(([k, v]) => [k, v * servings]));
  return (
    <table className="w-full text-sm" data-testid="nutrition-table">
      <tbody>
        {ROWS.map(([k, l, u]) => (
          <tr key={k} className={`border-b border-[#1A202C] ${k === "calories" ? "text-base" : ""}`}>
            <td className={`py-1.5 ${["sat_fat_g", "fiber_g", "sugar_g"].includes(k) ? "pl-4 text-[#A8A090]" : "text-[#E8DFD1]"}`}>{l}</td>
            <td className="py-1.5 text-right tabular-nums text-[#F3EBDD]" data-testid={`nutrition-${k}`}>{fmt(vals[k])}{u && <span className="ml-0.5 text-[#7A7468]">{u}</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const Breakdown = ({ data, names }) => (
  <details className="text-xs text-[#A8A090]" data-testid="nutrition-breakdown">
    <summary className="cursor-pointer select-none hover:text-[#F3EBDD]">How each ingredient adds up</summary>
    <ul className="mt-2 space-y-1.5">
      {data.items.map((it) => (
        <li key={it.id} className="flex gap-2">
          <span className="w-14 shrink-0 text-right tabular-nums text-[#F3EBDD]">{fmt(it.calories)} cal</span>
          <span className="min-w-0"><span className="text-[#E8DFD1]">{names[it.id] || "Removed ingredient"}</span>{it.guessed && <span className="ml-1 rounded bg-[#C084FC]/15 px-1 text-[10px] text-[#D8B4FE]">guessed</span>}<span className="block text-[#7A7468]">{it.basis}</span></span>
        </li>
      ))}
    </ul>
  </details>
);

export const NutritionPanel = ({ recipe, paid, servings }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("serving");
  const names = Object.fromEntries((recipe.ingredients || []).map((i) => [i.id, i.name]));
  const run = async (force = false) => {
    setBusy(true);
    try { setData((await api.post(`/recipes/${recipe.id}/nutrition`, { force })).data); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const tab = (k, l) => <button onClick={() => setMode(k)} className={`rounded-full px-2.5 py-0.5 text-xs ${mode === k ? "bg-[#D97706] text-[#0B0D10]" : "text-[#A8A090] hover:bg-[#1A202C]"}`} data-testid={`nutrition-mode-${k}`}>{l}</button>;
  return (
    <section className="space-y-3 rounded-lg border border-[#2D3442] bg-[#0F131A] p-5" data-testid="nutrition-panel">
      <div className="flex items-center gap-2"><HeartPulse className="h-4 w-4 text-[#F87171]" /><h3 className="flex-1 font-display text-xl text-[#F3EBDD]">Health stats</h3>
        {data && <button onClick={() => run(true)} disabled={busy} title="Estimate again" className="text-[#7A7468] hover:text-[#F3EBDD]" data-testid="nutrition-refresh"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /></button>}</div>
      {!paid ? <Locked what="AI health stats" testid="nutrition-locked" /> : !data ? (
        <div className="space-y-2">
          <p className="text-xs leading-relaxed text-[#A8A090]">Estimate calories, fat, sugar, protein and more from your ingredient list. Made-up ingredients are guessed from your descriptions.</p>
          <Button size="sm" onClick={() => run()} disabled={busy || !recipe.ingredients?.length} data-testid="nutrition-estimate"><Sparkles className="mr-1 h-3.5 w-3.5" />{busy ? "Estimating…" : "Estimate health stats"}</Button>
        </div>
      ) : (
        <div className="space-y-3 animate-in fade-in duration-300">
          <div className="flex gap-1">{tab("serving", "Per serving")}{tab("batch", `All ${servings} servings`)}</div>
          <Table data={data} mode={mode} servings={servings} />
          {data.missing?.length > 0 && <p className="text-[11px] text-[#FBBF24]" data-testid="nutrition-stale">Some ingredients weren't estimated. Tap refresh to try again.</p>}
          <Breakdown data={data} names={names} />
          {data.notes?.length > 0 && <ul className="list-disc space-y-0.5 pl-4 text-[11px] text-[#7A7468]">{data.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
          <p className="text-[10px] text-[#7A7468]">AI estimates for a portion of 1/{recipe.servings} of the recipe. Not medical or dietary advice.</p>
        </div>
      )}
    </section>
  );
};
